/**
 * Share target (PLAN.md §8, Faza 7).
 *
 * Android: "Udostępnij" -> GTD -> ta trasa z `?title=&text=&url=` (manifest
 * deklaruje share_target metodą GET, więc to zwykła nawigacja - działa też,
 * gdy service worker jeszcze nie wstał).
 *
 * Ekran jest celowo jednoprzyciskowy: capture ma trwać sekundę. Po sukcesie
 * krótkie potwierdzenie i powrót - do appki, która nas wywołała (`history.back`)
 * albo do chatu.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";

/** Tytuł, tekst i URL z intentu -> jedna wrzutka. */
export function buildSharedText(params: URLSearchParams): string {
  const title = (params.get("title") ?? "").trim();
  const text = (params.get("text") ?? "").trim();
  const url = (params.get("url") ?? "").trim();

  const parts: string[] = [];
  if (title) parts.push(title);
  // Android często wkłada URL do `text` - nie dublujemy go.
  if (text && text !== title) parts.push(text);
  if (url && !parts.some((part) => part.includes(url))) parts.push(url);
  return parts.join("\n").trim();
}

type State = "editing" | "saving" | "saved" | "queued" | "error";

export function SharePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initial = useMemo(() => buildSharedText(params), [params]);
  const [text, setText] = useState(initial);
  const [state, setState] = useState<State>("editing");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setText(initial), [initial]);

  // Po udanym zapisie znikamy z drogi: appka udostępniająca wraca na wierzch.
  useEffect(() => {
    if (state !== "saved" && state !== "queued") return;
    const timer = window.setTimeout(() => navigate("/chat", { replace: true }), 1200);
    return () => window.clearTimeout(timer);
  }, [state, navigate]);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    const value = text.trim();
    if (!value) return;
    setState("saving");
    setError(null);
    try {
      const result = await api.capture(value, "share");
      setState(result.queued ? "queued" : "saved");
    } catch (exc) {
      setState("error");
      setError(exc instanceof Error ? exc.message : String(exc));
    }
  }

  if (state === "saved" || state === "queued") {
    return (
      <div className="view share">
        <div className="share__done" role="status">
          <p className="share__check">✓</p>
          <p>
            {state === "saved"
              ? "Wrzucone do inboxu."
              : "Brak sieci — zapisane offline, wyślę po jej odzyskaniu."}
          </p>
          <button className="btn" onClick={() => navigate("/chat", { replace: true })}>
            Do chatu
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="view share">
      <h1 className="share__title">Do inboxu</h1>
      <form className="share__form" onSubmit={(event) => void save(event)}>
        <textarea
          className="share__text"
          value={text}
          rows={6}
          autoFocus
          aria-label="Treść wrzutki"
          placeholder="Co wrzucamy?"
          onChange={(event) => setText(event.target.value)}
        />
        {error && <p className="error">{error}</p>}
        <div className="clarify__buttons">
          <button
            className="btn btn--primary"
            type="submit"
            disabled={!text.trim() || state === "saving"}
          >
            {state === "saving" ? "Zapisuję…" : "Do inboxu"}
          </button>
          <button className="btn" type="button" onClick={() => navigate("/chat", { replace: true })}>
            Anuluj
          </button>
        </div>
      </form>
      <p className="muted">
        Clarify zrobimy później — teraz liczy się tylko to, żeby myśl nie uciekła.
      </p>
    </div>
  );
}
